import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { FIELDS_FOR_VALIDATE, FORM_NAME, REQUEST_ERROR_MESSAGE } from './constants';
import {
	GET_COMPANY_INFO,
	GET_ORG_TYPES,
	GET_TAX_RATES,
	GET_TAX_SYSTEMS,
	SEND_ACCREDITATION,
	setCompanyInfoAction,
	setIsCompanyInfoLoadingAction,
	setIsOrgTypesLoading,
	setIsSendingAccreditationAction,
	setIsTaxRatesLoading,
	setIsTaxSystemsLoading,
	setOrgTypesAction,
	setTaxRatesAction,
	setTaxSystemsAction,
} from './actions';
import {
	getCompanyRequest,
	getOrgTypesRequest,
	getTaxRatesRequest,
	getTaxSystemRequest,
	setCompanyAccreditationRequest,
} from '../../api/requests';
import { notificationInit } from '../../modules/notifications/actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForRequestSelector, getFormIsInvalidSelector } from './selectors';
import { getLocalStorageItem, setLocalStorageItem } from '../../utils/localStorage';
import { getUserInfoAction, setUserInfoAction } from '../profile/actions';
import { transformToValidationErrors } from './utils';

export function* getCompanyInfoSaga({ payload }) {
	try {
		yield put(setIsCompanyInfoLoadingAction(true));

		const { data, message, toast } = yield call(getCompanyRequest, payload.companyId);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setCompanyInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCompanyInfoLoadingAction(false));
	}
}

export function* getOrgTypesSaga() {
	try {
		yield put(setIsOrgTypesLoading(true));

		const { data, message, toast } = yield call(getOrgTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({ value: item.id, key: item.id, text: item.name }));

			yield put(setOrgTypesAction([...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrgTypesLoading(false));
	}
}

export function* getTaxSystemsSaga() {
	try {
		yield put(setIsTaxSystemsLoading(true));

		const { data, message, toast } = yield call(getTaxSystemRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({ value: item.id, key: item.id, text: item.name }));

			yield put(setTaxSystemsAction([...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTaxSystemsLoading(false));
	}
}

export function* getTaxRatesSaga({ payload }) {
	try {
		yield put(setIsTaxRatesLoading(true));

		const taxSystemId = payload?.taxSystemId;

		const path = taxSystemId ? { taxSystemId } : null;

		const { data, message, toast } = yield call(getTaxRatesRequest, path, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const mappedData = data.map(item => ({
				value: item.id,
				key: item.id,
				text: item.name_with_symbol,
				disabled: !item.is_available,
			}));

			yield put(setTaxRatesAction([...mappedData]));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTaxRatesLoading(false));
	}
}

export function* sendAccreditationSaga({ payload: { id, redirect } }) {
	try {
		yield put(setIsSendingAccreditationAction(true));

		const dataForRequest = yield select(getDataForRequestSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(
			setCompanyAccreditationRequest,
			id,
			dataForRequest?.company,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			if (data.filled_tax_at) {
				const user_data = getLocalStorageItem('user_data');

				setLocalStorageItem('user_data', { ...user_data, filled_tax_at: data.filled_tax_at });

				yield put(setUserInfoAction(data.user));

				yield put(getUserInfoAction());

				if (!user_data.isCompanyAccredited) {
					redirect('/company/accredited');
				} else {
					redirect('/home/information');
				}
			}
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsSendingAccreditationAction(false));
	}
}

export default function* accreditationCompanySaga() {
	yield takeEvery(GET_COMPANY_INFO, getCompanyInfoSaga);
	yield takeEvery(GET_ORG_TYPES, getOrgTypesSaga);
	yield takeEvery(GET_TAX_SYSTEMS, getTaxSystemsSaga);
	yield takeEvery(GET_TAX_RATES, getTaxRatesSaga);
	yield takeEvery(SEND_ACCREDITATION, sendAccreditationSaga);
}
